import { Component, ElementRef, OnDestroy, OnInit, ViewChild, AfterViewInit, AfterViewChecked, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { CallService } from '../../../services/call.service';
import { RemoteControlAdminService, RemoteControlSession, DesktopSession } from '../../../services/remote-control-admin.service';

const DEFAULT_WIDTH = 360;
const DEFAULT_HEIGHT = 400;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 200;
const MINIMIZED_WIDTH = 220;
const MINIMIZED_HEIGHT = 48;

@Component({
  selector: 'app-call-overlay',
  templateUrl: './call-overlay.component.html',
  styleUrls: ['./call-overlay.component.scss']
})
export class CallOverlayComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('localVideo') localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('posVideo') posVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('desktopFrame') desktopFrameRef!: ElementRef<HTMLImageElement>;

  callState$ = this.callService.callState$;
  remoteStream$ = this.callService.remoteStream$;
  localStream$ = this.callService.localStream$;
  isLocalMuted$ = this.callService.isLocalMuted$;
  isLocalCameraOff$ = this.callService.isLocalCameraOff$;
  screenSharing$ = this.callService.screenSharing$;

  posSession: RemoteControlSession | null = null;
  posDesktopSession: DesktopSession | null = null;
  currentFrameSrc: string | null = null;

  minimized = false;
  isScreenSharing = false;

  // Overlay position and size (pixels)
  overlayTop = 0;
  overlayLeft = 0;
  overlayWidth = DEFAULT_WIDTH;
  overlayHeight = DEFAULT_HEIGHT;

  // Interaction state flags
  isDragging = false;
  isResizing = false;
  resizeDirection = '';

  // Pre-minimize state for restoring after expand
  private preMinimizeWidth = DEFAULT_WIDTH;
  private preMinimizeHeight = DEFAULT_HEIGHT;
  private preMinimizeTop = 0;
  private preMinimizeLeft = 0;

  // Drag bookkeeping
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartLeft = 0;
  private dragStartTop = 0;

  // Resize bookkeeping
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartTop = 0;
  private resizeStartLeft = 0;

  // Pending POS stream: set when stream arrives before posVideoRef exists
  private pendingPosStream: MediaStream | null = null;

  private subscriptions: Subscription[] = [];

  constructor(
    public callService: CallService,
    public remoteControlService: RemoteControlAdminService,
  ) {}

  ngOnInit(): void {
    this.resetPosition();
    this.subscriptions.push(
      this.callState$.subscribe(state => {
        if (state === 'ringing-out' || state === 'connecting') {
          this.resetPosition();
          this.minimized = false;
        }
      }),
      this.remoteControlService.activeSession$.subscribe(session => {
        this.posSession = session;
        if (session === null) {
          this.pendingPosStream = null;
          if (this.posVideoRef?.nativeElement) {
            this.posVideoRef.nativeElement.srcObject = null;
          }
        } else if (session.stream) {
          if (this.posVideoRef?.nativeElement) {
            this.posVideoRef.nativeElement.srcObject = session.stream;
            this.pendingPosStream = null;
          } else {
            this.pendingPosStream = session.stream;
          }
          if (this.minimized) {
            this.minimized = false;
            this.resetPosition();
          }
        }
      }),
      this.remoteControlService.activeDesktopSession$.subscribe(session => {
        this.posDesktopSession = session;
        if (session !== null && this.minimized) {
          this.minimized = false;
          this.resetPosition();
        }
      }),
      this.remoteControlService.currentFrameSrc$.subscribe(src => {
        this.currentFrameSrc = src;
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.localStream$.subscribe(stream => {
        if (this.localVideoRef?.nativeElement && stream) {
          this.localVideoRef.nativeElement.srcObject = stream;
        }
      }),
      this.remoteStream$.subscribe(stream => {
        if (this.remoteVideoRef?.nativeElement && stream) {
          this.remoteVideoRef.nativeElement.srcObject = stream;
        }
      }),
      this.screenSharing$.subscribe(val => {
        this.isScreenSharing = val;
      })
    );
  }

  ngAfterViewChecked(): void {
    if (this.pendingPosStream && this.posVideoRef?.nativeElement && !this.posVideoRef.nativeElement.srcObject) {
      this.posVideoRef.nativeElement.srcObject = this.pendingPosStream;
      this.pendingPosStream = null;
    }
  }

  get isVisible(): boolean {
    return this.callState$.getValue() !== 'idle' || this.posSession !== null || this.posDesktopSession !== null;
  }

  get overlayStyle(): { [key: string]: string } {
    return {
      top: this.overlayTop + 'px',
      left: this.overlayLeft + 'px',
      width: this.overlayWidth + 'px',
      height: (this.minimized ? MINIMIZED_HEIGHT : this.overlayHeight) + 'px'
    };
  }

  get headerLabel(): string {
    if (this.posDesktopSession !== null) {
      return this.currentFrameSrc ? 'Desktop View' : 'Desktop Connecting...';
    }
    if (this.posSession !== null) {
      return this.posSession.stream ? 'POS Remote Control' : 'POS Connecting...';
    }
    const state = this.callState$.getValue();
    return state.charAt(0).toUpperCase() + state.slice(1);
  }

  private resetPosition(): void {
    this.overlayWidth = DEFAULT_WIDTH;
    this.overlayHeight = DEFAULT_HEIGHT;
    this.overlayTop = window.innerHeight - DEFAULT_HEIGHT - 20;
    this.overlayLeft = window.innerWidth - DEFAULT_WIDTH - 20;
  }

  toggleMinimized(): void {
    if (!this.minimized) {
      this.preMinimizeWidth = this.overlayWidth;
      this.preMinimizeHeight = this.overlayHeight;
      this.preMinimizeTop = this.overlayTop;
      this.preMinimizeLeft = this.overlayLeft;
      this.overlayWidth = MINIMIZED_WIDTH;
      this.overlayTop = window.innerHeight - MINIMIZED_HEIGHT - 20;
      this.overlayLeft = window.innerWidth - MINIMIZED_WIDTH - 20;
    } else {
      this.overlayWidth = this.preMinimizeWidth;
      this.overlayHeight = this.preMinimizeHeight;
      this.overlayTop = this.preMinimizeTop;
      this.overlayLeft = this.preMinimizeLeft;
    }
    this.minimized = !this.minimized;
  }

  onDragStart(event: MouseEvent): void {
    if (this.minimized) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartLeft = this.overlayLeft;
    this.dragStartTop = this.overlayTop;
  }

  onResizeStart(event: MouseEvent, direction: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.resizeDirection = direction;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.overlayWidth;
    this.resizeStartHeight = this.overlayHeight;
    this.resizeStartTop = this.overlayTop;
    this.resizeStartLeft = this.overlayLeft;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;
      const maxLeft = window.innerWidth - this.overlayWidth;
      const maxTop = window.innerHeight - (this.minimized ? MINIMIZED_HEIGHT : this.overlayHeight);
      this.overlayLeft = Math.max(0, Math.min(maxLeft, this.dragStartLeft + dx));
      this.overlayTop = Math.max(0, Math.min(maxTop, this.dragStartTop + dy));
    } else if (this.isResizing) {
      this.handleResize(event);
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = '';
  }

  private handleResize(event: MouseEvent): void {
    const dx = event.clientX - this.resizeStartX;
    const dy = event.clientY - this.resizeStartY;
    const maxW = window.innerWidth * 0.9;
    const maxH = window.innerHeight * 0.9;
    const dir = this.resizeDirection;

    let newWidth = this.resizeStartWidth;
    let newHeight = this.resizeStartHeight;
    let newTop = this.resizeStartTop;
    let newLeft = this.resizeStartLeft;

    if (dir.includes('e')) {
      newWidth = Math.max(MIN_WIDTH, Math.min(maxW, this.resizeStartWidth + dx));
    }
    if (dir.includes('s')) {
      newHeight = Math.max(MIN_HEIGHT, Math.min(maxH, this.resizeStartHeight + dy));
    }
    if (dir.includes('w')) {
      const possibleWidth = Math.max(MIN_WIDTH, Math.min(maxW, this.resizeStartWidth - dx));
      newLeft = this.resizeStartLeft + (this.resizeStartWidth - possibleWidth);
      newWidth = possibleWidth;
    }
    if (dir.includes('n')) {
      const possibleHeight = Math.max(MIN_HEIGHT, Math.min(maxH, this.resizeStartHeight - dy));
      newTop = this.resizeStartTop + (this.resizeStartHeight - possibleHeight);
      newHeight = possibleHeight;
    }

    this.overlayWidth = newWidth;
    this.overlayHeight = newHeight;
    this.overlayTop = newTop;
    this.overlayLeft = newLeft;
  }

  toggleScreenShare(): void {
    if (this.isScreenSharing) {
      this.callService.stopScreenShare();
    } else {
      this.callService.startScreenShare();
    }
  }

  onPosMouseEvent(event: MouseEvent): void {
    if (!this.posSession || !this.posVideoRef?.nativeElement) return;
    if (event.type !== 'mousemove') {
      console.log('[RC Admin] onPosMouseEvent:', event.type, 'controlId:', this.posSession?.controlId, 'videoRef:', !!this.posVideoRef?.nativeElement);
    }
    event.preventDefault();
    this.remoteControlService.sendInputEvent(
      this.posSession.controlId,
      event,
      this.posVideoRef.nativeElement,
    );
  }

  onPosKeyEvent(event: KeyboardEvent): void {
    if (!this.posSession) return;
    event.preventDefault();
    const videoEl = this.posVideoRef?.nativeElement;
    if (!videoEl) return;
    this.remoteControlService.sendInputEvent(this.posSession.controlId, event, videoEl);
  }

  onPosWheelEvent(event: WheelEvent): void {
    if (!this.posSession || !this.posVideoRef?.nativeElement) return;
    event.preventDefault();
    this.remoteControlService.sendInputEvent(
      this.posSession.controlId,
      event,
      this.posVideoRef.nativeElement,
    );
  }

  onDesktopMouseEvent(event: MouseEvent): void {
    if (!this.posDesktopSession || !this.desktopFrameRef?.nativeElement) return;
    event.preventDefault();
    this.remoteControlService.sendDesktopInput(
      this.posDesktopSession.controlId,
      event,
      this.desktopFrameRef.nativeElement,
      this.posDesktopSession.screenWidth,
      this.posDesktopSession.screenHeight,
    );
  }

  onDesktopWheelEvent(event: WheelEvent): void {
    if (!this.posDesktopSession || !this.desktopFrameRef?.nativeElement) return;
    event.preventDefault();
    this.remoteControlService.sendDesktopInput(
      this.posDesktopSession.controlId,
      event,
      this.desktopFrameRef.nativeElement,
      this.posDesktopSession.screenWidth,
      this.posDesktopSession.screenHeight,
    );
  }

  onDesktopKeyEvent(event: KeyboardEvent): void {
    if (!this.posDesktopSession) return;
    event.preventDefault();
    const imgEl = this.desktopFrameRef?.nativeElement;
    if (!imgEl) return;
    this.remoteControlService.sendDesktopInput(
      this.posDesktopSession.controlId,
      event,
      imgEl,
      this.posDesktopSession.screenWidth,
      this.posDesktopSession.screenHeight,
    );
  }

  endDesktopSession(): void {
    this.remoteControlService.endDesktopSession();
  }

  endPosSession(): void {
    this.remoteControlService.endSession();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
